import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, KundaliChartData } from '../types/kundali';
import { VargaKey } from '../types';
import { CurrentPhaseForecastView } from './CurrentPhaseForecastView';
import { DashaTimeline } from './DashaTimeline';
import { 
  LayoutGrid, 
  Maximize2, 
  Minimize2, 
  Compass, 
  Sparkles, 
  Calendar, 
  Clock, 
  Eye, 
  Layers, 
  ChevronLeft, 
  ChevronRight, 
  RotateCcw, 
  Grid, 
  Columns, 
  Square, 
  Sun, 
  Moon, 
  Activity, 
  Award, 
  Briefcase, 
  Heart, 
  TrendingUp, 
  Zap, 
  CheckCircle2, 
  Info,
  Sliders,
  Filter
} from 'lucide-react';

export type PaneContentType = 
  | 'D1' | 'D9' | 'D10' | 'D7' | 'D4' | 'D12' | 'D60' 
  | 'TRANSIT' | 'CHANDRA' | 'BHAVA_CHALIT'
  | 'DASHA_TREE' | 'FORECAST_DOMAINS' | 'ASHTAKVARGA' | 'PLANET_TABLE';

export interface PaneConfig {
  id: number;
  contentType: PaneContentType;
}

interface DynamicMultiViewCanvasProps {
  kundali: CompleteKundali;
  lang?: Language;
  className?: string;
}

const RASHI_NAMES_EN = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const RASHI_NAMES_HI = ['मेष', 'वृषभ', 'मिथुन', 'कर्क', 'सिंह', 'कन्या', 'तुला', 'वृश्चिक', 'धनु', 'मकर', 'कुंभ', 'मीन'];

export const DynamicMultiViewCanvas: React.FC<DynamicMultiViewCanvasProps> = ({
  kundali,
  lang = 'en',
  className = ''
}) => {
  const isHi = lang === 'hi';

  // 1. Grid State (1 to 4 Panes)
  const [paneCount, setPaneCount] = useState<1 | 2 | 3 | 4>(3);
  const [chartStyle, setChartStyle] = useState<'NORTH' | 'SOUTH'>('NORTH');
  const [highlightedPlanet, setHighlightedPlanet] = useState<string | null>(null);
  const [maximizedPaneId, setMaximizedPaneId] = useState<number | null>(null);

  // Time Travel Date Scrubber State
  const [referenceDate, setReferenceDate] = useState<Date>(new Date('2026-09-23T01:00:00Z'));

  // Default 4 Panes Configuration
  const [panes, setPanes] = useState<PaneConfig[]>([
    { id: 1, contentType: 'D1' },
    { id: 2, contentType: 'D9' },
    { id: 3, contentType: 'DASHA_TREE' },
    { id: 4, contentType: 'FORECAST_DOMAINS' }
  ]);

  // 2. Pro Astrologer Presets
  const applyPreset = (presetName: 'DASHA_NAVAMSHA' | 'CAREER' | 'MARRIAGE' | 'FULL_QUAD') => {
    if (presetName === 'DASHA_NAVAMSHA') {
      setPaneCount(3);
      setPanes([
        { id: 1, contentType: 'D1' },
        { id: 2, contentType: 'D9' },
        { id: 3, contentType: 'DASHA_TREE' },
        { id: 4, contentType: 'FORECAST_DOMAINS' }
      ]);
    } else if (presetName === 'CAREER') {
      setPaneCount(3);
      setPanes([
        { id: 1, contentType: 'D1' },
        { id: 2, contentType: 'D10' },
        { id: 3, contentType: 'DASHA_TREE' },
        { id: 4, contentType: 'FORECAST_DOMAINS' }
      ]);
    } else if (presetName === 'MARRIAGE') {
      setPaneCount(3);
      setPanes([
        { id: 1, contentType: 'D1' },
        { id: 2, contentType: 'D9' },
        { id: 3, contentType: 'DASHA_TREE' },
        { id: 4, contentType: 'FORECAST_DOMAINS' }
      ]);
    } else if (presetName === 'FULL_QUAD') {
      setPaneCount(4);
      setPanes([
        { id: 1, contentType: 'D1' },
        { id: 2, contentType: 'D9' },
        { id: 3, contentType: 'TRANSIT' },
        { id: 4, contentType: 'DASHA_TREE' }
      ]);
    }
  };

  // Date Scrubber Handlers
  const handleShiftDate = (days: number) => {
    const next = new Date(referenceDate.getTime() + days * 86400000);
    setReferenceDate(next);
  };

  const handleResetDate = () => {
    setReferenceDate(new Date('2026-09-23T01:00:00Z'));
  };

  const updatePaneContent = (paneId: number, type: PaneContentType) => {
    setPanes(prev => prev.map(p => p.id === paneId ? { ...p, contentType: type } : p));
  };

  // Helper to extract planets per house for a given chart type
  const getHouseDataForPane = (type: PaneContentType) => {
    let chartData: KundaliChartData | undefined;
    if (type === 'D1') chartData = kundali.charts?.D1;
    else if (type === 'D9') chartData = kundali.charts?.D9;
    else if (type === 'D10') chartData = kundali.charts?.D10;
    else if (type === 'D7') chartData = kundali.charts?.D7;
    else if (type === 'D4') chartData = kundali.charts?.D4;
    else if (type === 'D12') chartData = kundali.charts?.D12;
    else if (type === 'D60') chartData = kundali.charts?.D60;
    else if (type === 'TRANSIT') chartData = kundali.charts?.Transit;
    else if (type === 'CHANDRA') chartData = kundali.charts?.Chandra;
    else if (type === 'BHAVA_CHALIT') chartData = kundali.charts?.Chalit;

    const ascSign = chartData 
      ? (chartData.ascendantIndex + 1)
      : ((kundali.houses.find(h => h.houseNumber === 1)?.signIndex ?? 0) + 1);

    const planets = chartData?.planets || kundali.planets || [];

    // Map planets to houses
    const houseMap: { [houseNum: number]: { name: string; isRetro?: boolean; dignity?: string }[] } = {};
    for (let h = 1; h <= 12; h++) houseMap[h] = [];

    planets.forEach((p) => {
      const targetHouse = p.house || 1;
      houseMap[targetHouse].push({
        name: p.name,
        isRetro: p.isRetrograde,
        dignity: p.dignity === 'Exalted' ? 'E' : p.dignity === 'Debilitated' ? 'D' : p.dignity === 'Own House' ? 'O' : undefined
      });
    });

    return { ascSign, houseMap };
  };

  // Render SVG Chart for North or South Style
  const renderSvgChart = (type: PaneContentType) => {
    const { ascSign, houseMap } = getHouseDataForPane(type);

    if (chartStyle === 'NORTH') {
      // North Indian Diamond Layout SVG
      return (
        <svg viewBox="0 0 300 300" className="w-full h-full max-h-[320px] mx-auto select-none">
          {/* Background Outer Border */}
          <rect x="5" y="5" width="290" height="290" fill="#FAF6ED" stroke="#8C6A38" strokeWidth="2" />
          
          {/* Inner Cross Lines forming Diamonds */}
          <line x1="5" y1="5" x2="295" y2="295" stroke="#B89458" strokeWidth="1.2" />
          <line x1="295" y1="5" x2="5" y2="295" stroke="#B89458" strokeWidth="1.2" />
          
          <polygon points="150,5 295,150 150,295 5,150" fill="none" stroke="#8C6A38" strokeWidth="1.5" />

          {/* House 1 (Top Center Diamond) */}
          <g>
            <text x="150" y="25" textAnchor="middle" className="text-[10px] font-mono font-extrabold fill-amber-900">
              {ascSign}
            </text>
            <text x="150" y="70" textAnchor="middle" className="text-[11px] font-serif font-bold fill-amber-950">
              H1
            </text>
            {/* Planets in H1 */}
            {houseMap[1]?.map((pl, idx) => {
              const isGlow = highlightedPlanet === pl.name;
              return (
                <text 
                  key={idx} 
                  x="150" 
                  y={85 + idx * 14} 
                  textAnchor="middle" 
                  onClick={() => setHighlightedPlanet(highlightedPlanet === pl.name ? null : pl.name)}
                  className={`text-[11px] font-bold cursor-pointer transition-all ${
                    isGlow ? 'fill-rose-600 font-extrabold underline text-[12px]' : 'fill-astro-primary'
                  }`}
                >
                  {pl.name.substring(0, 2)}{pl.isRetro ? '(R)' : ''}{pl.dignity ? `[${pl.dignity}]` : ''}
                </text>
              );
            })}
          </g>

          {/* House 2 (Top Left) */}
          <text x="75" y="40" textAnchor="middle" className="text-[10px] font-mono fill-amber-900">
            {((ascSign + 0) % 12) + 1}
          </text>

          {/* House 4 (Left Diamond) */}
          <g>
            <text x="30" y="150" textAnchor="middle" className="text-[10px] font-mono fill-amber-900">
              {((ascSign + 2) % 12) + 1}
            </text>
            <text x="75" y="150" textAnchor="middle" className="text-[11px] font-bold fill-amber-950">
              H4
            </text>
            {houseMap[4]?.map((pl, idx) => (
              <text 
                key={idx} 
                x="75" 
                y={165 + idx * 14} 
                textAnchor="middle"
                className={`text-[11px] font-bold cursor-pointer ${
                  highlightedPlanet === pl.name ? 'fill-rose-600 font-extrabold' : 'fill-astro-primary'
                }`}
              >
                {pl.name.substring(0, 2)}{pl.isRetro ? '(R)' : ''}
              </text>
            ))}
          </g>

          {/* House 7 (Bottom Diamond) */}
          <g>
            <text x="150" y="275" textAnchor="middle" className="text-[10px] font-mono fill-amber-900">
              {((ascSign + 5) % 12) + 1}
            </text>
            <text x="150" y="220" textAnchor="middle" className="text-[11px] font-bold fill-amber-950">
              H7
            </text>
            {houseMap[7]?.map((pl, idx) => (
              <text 
                key={idx} 
                x="150" 
                y={235 + idx * 14} 
                textAnchor="middle"
                className={`text-[11px] font-bold cursor-pointer ${
                  highlightedPlanet === pl.name ? 'fill-rose-600 font-extrabold' : 'fill-astro-primary'
                }`}
              >
                {pl.name.substring(0, 2)}{pl.isRetro ? '(R)' : ''}
              </text>
            ))}
          </g>

          {/* House 10 (Right Diamond) */}
          <g>
            <text x="270" y="150" textAnchor="middle" className="text-[10px] font-mono fill-amber-900">
              {((ascSign + 8) % 12) + 1}
            </text>
            <text x="225" y="150" textAnchor="middle" className="text-[11px] font-bold fill-amber-950">
              H10
            </text>
            {houseMap[10]?.map((pl, idx) => (
              <text 
                key={idx} 
                x="225" 
                y={165 + idx * 14} 
                textAnchor="middle"
                className={`text-[11px] font-bold cursor-pointer ${
                  highlightedPlanet === pl.name ? 'fill-rose-600 font-extrabold' : 'fill-astro-primary'
                }`}
              >
                {pl.name.substring(0, 2)}{pl.isRetro ? '(R)' : ''}
              </text>
            ))}
          </g>
        </svg>
      );
    } else {
      // South Indian Grid Layout SVG
      return (
        <svg viewBox="0 0 300 300" className="w-full h-full max-h-[320px] mx-auto select-none">
          {/* Outer Border */}
          <rect x="5" y="5" width="290" height="290" fill="#FAF6ED" stroke="#8C6A38" strokeWidth="2" />
          
          {/* Grid Lines for 4x4 Grid */}
          <line x1="77.5" y1="5" x2="77.5" y2="295" stroke="#B89458" strokeWidth="1" />
          <line x1="150" y1="5" x2="150" y2="295" stroke="#B89458" strokeWidth="1" />
          <line x1="222.5" y1="5" x2="222.5" y2="295" stroke="#B89458" strokeWidth="1" />

          <line x1="5" y1="77.5" x2="295" y2="77.5" stroke="#B89458" strokeWidth="1" />
          <line x1="5" y1="150" x2="295" y2="150" stroke="#B89458" strokeWidth="1" />
          <line x1="5" y1="222.5" x2="295" y2="222.5" stroke="#B89458" strokeWidth="1" />

          {/* Center Box Cover */}
          <rect x="77.5" y="77.5" width="145" height="145" fill="#F4EBD9" stroke="#8C6A38" strokeWidth="1.5" />
          <text x="150" y="145" textAnchor="middle" className="text-[12px] font-bold font-serif fill-amber-950">
            {type} South Chart
          </text>
          <text x="150" y="165" textAnchor="middle" className="text-[10px] fill-amber-800">
            Lagna: {RASHI_NAMES_EN[ascSign - 1]}
          </text>

          {/* Render Lagna & Planets in Fixed Zodiac Boxes */}
          {/* Pisces Box (Top Left) */}
          <text x="15" y="20" className="text-[9px] font-bold fill-amber-900">Pisces</text>
          {ascSign === 12 && <text x="60" y="20" className="text-[9px] font-extrabold fill-rose-600">ASC</text>}

          {/* Aries Box */}
          <text x="90" y="20" className="text-[9px] font-bold fill-amber-900">Aries</text>
          {ascSign === 1 && <text x="135" y="20" className="text-[9px] font-extrabold fill-rose-600">ASC</text>}

          {/* Taurus Box */}
          <text x="160" y="20" className="text-[9px] font-bold fill-amber-900">Taurus</text>
          {ascSign === 2 && <text x="205" y="20" className="text-[9px] font-extrabold fill-rose-600">ASC</text>}
        </svg>
      );
    }
  };

  // Render Content inside Pane Box
  const renderPaneContent = (type: PaneContentType) => {
    switch (type) {
      case 'DASHA_TREE':
        return (
          <div className="h-full overflow-y-auto custom-scrollbar p-2">
            <DashaTimeline kundali={kundali} lang={lang} />
          </div>
        );

      case 'FORECAST_DOMAINS':
        return (
          <div className="h-full overflow-y-auto custom-scrollbar p-2">
            <CurrentPhaseForecastView kundali={kundali} lang={lang} defaultViewMode="pro" />
          </div>
        );

      case 'ASHTAKVARGA':
        return (
          <div className="p-4 space-y-3 bg-astro-card rounded-xl border border-astro-border text-xs">
            <h4 className="font-bold text-astro-primary font-serif">Sarvashtakavarga (SAV) Matrix</h4>
            <div className="grid grid-cols-4 gap-2">
              {kundali.houses.map((h) => {
                const binduCount =
                  kundali.ashtakvarga?.byHouse?.sarvashtakvarga?.[h.houseNumber - 1] ??
                  kundali.ashtakvarga?.sarvashtakvarga?.[h.signIndex] ??
                  28;
                return (
                  <div key={h.houseNumber} className="bg-astro-bg border border-astro-border p-2.5 rounded-lg text-center">
                    <span className="text-[10px] text-astro-secondary-text block">House #{h.houseNumber}</span>
                    <span className="text-sm font-bold text-astro-primary">{binduCount} Bindus</span>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'PLANET_TABLE':
        return (
          <div className="p-3 bg-astro-card rounded-xl border border-astro-border text-xs overflow-x-auto">
            <h4 className="font-bold text-astro-primary font-serif mb-2">Planetary Degrees & Nakshatra Table</h4>
            <table className="w-full text-left border-collapse text-[11px]">
              <thead>
                <tr className="border-b border-astro-border bg-astro-bg font-bold text-astro-secondary-text">
                  <th className="p-1.5">Graha</th>
                  <th className="p-1.5">Rashi</th>
                  <th className="p-1.5">Degree</th>
                  <th className="p-1.5">Nakshatra</th>
                  <th className="p-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {kundali.planets?.map((p) => (
                  <tr 
                    key={p.name}
                    onClick={() => setHighlightedPlanet(highlightedPlanet === p.name ? null : p.name)}
                    className={`border-b border-astro-border/50 cursor-pointer transition-colors ${
                      highlightedPlanet === p.name ? 'bg-rose-100 font-bold text-rose-900' : 'hover:bg-astro-bg'
                    }`}
                  >
                    <td className="p-1.5 font-bold">{p.name}</td>
                    <td className="p-1.5">{p.sign}</td>
                    <td className="p-1.5 font-mono">{p.degreeInSign ? p.degreeInSign.toFixed(2) : '0.00'}°</td>
                    <td className="p-1.5">{p.nakshatra} (Pada {p.pada})</td>
                    <td className="p-1.5">
                      {p.isRetrograde ? 'R ' : ''}
                      {p.dignity || 'Direct'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      default:
        // Render Divisional / Transit Chart
        return (
          <div className="p-3 bg-astro-card rounded-xl border border-astro-border space-y-2 flex flex-col justify-between h-full">
            <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
              <span className="flex items-center gap-1">
                <Compass className="w-3.5 h-3.5 text-astro-accent" />
                {type === 'TRANSIT' ? 'Gochar Transit Chart' : `${type} Divisional Chart`}
              </span>
              <span className="text-[10px] bg-astro-primary/10 text-astro-primary px-2 py-0.5 rounded font-mono">
                {chartStyle} Indian
              </span>
            </div>

            <div className="flex-1 flex items-center justify-center py-2">
              {renderSvgChart(type)}
            </div>

            <div className="text-[10px] text-astro-secondary-text text-center border-t border-astro-border/50 pt-1.5">
              Click any planet to cross-highlight across all open panes
            </div>
          </div>
        );
    }
  };

  // Determine Active Grid Layout CSS Class
  const getGridClass = () => {
    if (paneCount === 1) return 'grid-cols-1';
    if (paneCount === 2) return 'grid-cols-1 lg:grid-cols-2';
    if (paneCount === 3) return 'grid-cols-1 lg:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2';
  };

  return (
    <div className={`bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-6 shadow-sm space-y-5 ${className}`}>
      
      {/* HEADER BAR: PRO PRESETS & WORKBENCH CONTROLS */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-astro-border pb-4">
        <div>
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-astro-accent" />
            <h2 className="text-base sm:text-lg font-bold text-astro-primary font-serif">
              {isHi ? 'डायनामिक मल्टी-व्यू चार्ट एवं विश्लेषण ग्रिड' : 'Dynamic Multi-View Canvas Workbench'}
            </h2>
            <span className="px-2.5 py-0.5 rounded-full bg-astro-accent/20 text-astro-accent font-bold text-[10px] border border-astro-accent/30">
              V6.1 Pro Suite
            </span>
          </div>
          <p className="text-xs text-astro-secondary-text mt-0.5">
            {isHi
              ? 'चार्ट (D1-D60) व विश्लेषण फलकों की स्वतंत्र ग्रिड, क्रॉस-हाईलाइटिंग एवं टाइम ट्रैवल'
              : 'Flexible 1–4 Pane Canvas Grid with Cross-Highlighting, Time Scrubber & Pro Presets'}
          </p>
        </div>

        {/* Pro Astrologer Preset Shortcuts */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-astro-secondary-text hidden sm:inline">Presets:</span>
          <button
            onClick={() => applyPreset('DASHA_NAVAMSHA')}
            className="px-2.5 py-1.5 rounded-lg bg-astro-card border border-astro-border hover:border-astro-primary text-xs font-bold text-astro-primary transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
          >
            🎯 {isHi ? 'दशा + नवांरा' : 'Dasha + D9'}
          </button>
          <button
            onClick={() => applyPreset('CAREER')}
            className="px-2.5 py-1.5 rounded-lg bg-astro-card border border-astro-border hover:border-astro-primary text-xs font-bold text-astro-primary transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
          >
            💼 {isHi ? 'करियर D10' : 'Career D10'}
          </button>
          <button
            onClick={() => applyPreset('MARRIAGE')}
            className="px-2.5 py-1.5 rounded-lg bg-astro-card border border-astro-border hover:border-astro-primary text-xs font-bold text-astro-primary transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
          >
            💍 {isHi ? 'विवाह D9' : 'Marriage D9'}
          </button>
          <button
            onClick={() => applyPreset('FULL_QUAD')}
            className="px-2.5 py-1.5 rounded-lg bg-astro-card border border-astro-border hover:border-astro-primary text-xs font-bold text-astro-primary transition-all flex items-center gap-1 cursor-pointer shadow-2xs"
          >
            🌐 {isHi ? 'पूर्ण ग्रिड (4-Pane)' : 'Full 4-Pane Grid'}
          </button>
        </div>
      </div>

      {/* SECONDARY TOOLBAR: PANE COUNT, CHART STYLE & TIME MACHINE SCRUBBER */}
      <div className="bg-astro-card border border-astro-border rounded-xl p-3 flex flex-wrap items-center justify-between gap-3 text-xs">
        
        {/* Pane Count Selector */}
        <div className="flex items-center gap-2">
          <span className="font-bold text-astro-primary">{isHi ? 'ग्रिड विभाजन:' : 'Grid Panes:'}</span>
          <div className="flex items-center gap-1 bg-astro-bg p-1 rounded-lg border border-astro-border">
            {([1, 2, 3, 4] as (1|2|3|4)[]).map((num) => (
              <button
                key={num}
                onClick={() => setPaneCount(num)}
                className={`w-7 h-7 rounded-md font-bold transition-all cursor-pointer ${
                  paneCount === num
                    ? 'bg-astro-primary text-white shadow-2xs'
                    : 'text-astro-secondary-text hover:text-astro-primary'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {/* Chart Style Switcher (North vs South Indian) */}
        <div className="flex items-center gap-2">
          <span className="font-bold text-astro-primary">{isHi ? 'शैली:' : 'Chart Style:'}</span>
          <div className="flex items-center gap-1 bg-astro-bg p-1 rounded-lg border border-astro-border">
            <button
              onClick={() => setChartStyle('NORTH')}
              className={`px-2.5 py-1 rounded-md font-bold transition-all cursor-pointer ${
                chartStyle === 'NORTH'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              ♢ North Indian
            </button>
            <button
              onClick={() => setChartStyle('SOUTH')}
              className={`px-2.5 py-1 rounded-md font-bold transition-all cursor-pointer ${
                chartStyle === 'SOUTH'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              ▢ South Indian
            </button>
          </div>
        </div>

        {/* Time Travel Date Scrubber */}
        <div className="flex items-center gap-1.5 bg-astro-bg px-3 py-1.5 rounded-lg border border-astro-border font-mono">
          <Clock className="w-3.5 h-3.5 text-astro-accent shrink-0" />
          <button 
            onClick={() => handleShiftDate(-365)} 
            className="px-1.5 py-0.5 rounded hover:bg-astro-card font-bold text-astro-primary cursor-pointer"
            title="Minus 1 Year"
          >
            -1Y
          </button>
          <button 
            onClick={() => handleShiftDate(-30)} 
            className="px-1.5 py-0.5 rounded hover:bg-astro-card font-bold text-astro-primary cursor-pointer"
            title="Minus 1 Month"
          >
            -1M
          </button>

          <span className="font-bold text-astro-primary px-1">
            {referenceDate.toLocaleDateString(isHi ? 'hi-IN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>

          <button 
            onClick={() => handleShiftDate(30)} 
            className="px-1.5 py-0.5 rounded hover:bg-astro-card font-bold text-astro-primary cursor-pointer"
            title="Plus 1 Month"
          >
            +1M
          </button>
          <button 
            onClick={() => handleShiftDate(365)} 
            className="px-1.5 py-0.5 rounded hover:bg-astro-card font-bold text-astro-primary cursor-pointer"
            title="Plus 1 Year"
          >
            +1Y
          </button>
          <button 
            onClick={handleResetDate} 
            className="p-1 rounded hover:bg-astro-card text-astro-accent cursor-pointer"
            title="Reset to Current Date"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>

      </div>

      {/* Active Cross-Highlight Indicator Bar */}
      {highlightedPlanet && (
        <div className="bg-rose-100 border border-rose-300 text-rose-900 px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-rose-600 animate-pulse" />
            <span>Active Cross-Highlighting: <strong>{highlightedPlanet}</strong> is highlighted across all open chart panes</span>
          </span>
          <button 
            onClick={() => setHighlightedPlanet(null)}
            className="underline text-[11px] hover:text-rose-950 cursor-pointer"
          >
            Clear Highlight
          </button>
        </div>
      )}

      {/* CANVAS GRID CONTAINER */}
      <div className={`grid gap-4 ${getGridClass()}`}>
        {panes.slice(0, paneCount).map((pane) => (
          <div 
            key={pane.id}
            className="bg-astro-bg border border-astro-border rounded-2xl p-3 shadow-2xs space-y-3 flex flex-col justify-between min-h-[400px]"
          >
            {/* Pane Selector Header Bar */}
            <div className="flex items-center justify-between gap-2 border-b border-astro-border pb-2 bg-astro-card p-2 rounded-xl">
              <div className="flex items-center gap-1.5">
                <span className="w-5 h-5 rounded-md bg-astro-primary text-white font-bold text-[10px] flex items-center justify-center">
                  #{pane.id}
                </span>
                
                {/* Pane Content Type Dropdown */}
                <select
                  value={pane.contentType}
                  onChange={(e) => updatePaneContent(pane.id, e.target.value as PaneContentType)}
                  className="bg-transparent font-bold text-xs text-astro-primary focus:outline-hidden cursor-pointer"
                >
                  <optgroup label="Divisional Charts (vargas)">
                    <option value="D1">D1 Rashi Chart</option>
                    <option value="D9">D9 Navamsha Chart</option>
                    <option value="D10">D10 Dashamsha (Career)</option>
                    <option value="D7">D7 Saptamsha (Children)</option>
                    <option value="D4">D4 Chaturthamsha (Property)</option>
                    <option value="D12">D12 Dwadasamsha (Parents)</option>
                    <option value="D60">D60 Shashtiamsha (Micro Karma)</option>
                  </optgroup>
                  <optgroup label="Special & Transit Charts">
                    <option value="TRANSIT">Gochar Transit Chart</option>
                    <option value="CHANDRA">Chandra Kundali (Moon)</option>
                    <option value="BHAVA_CHALIT">Bhava Chalit Chart</option>
                  </optgroup>
                  <optgroup label="Telemetry & Analysis Panes">
                    <option value="DASHA_TREE">Vimshottari Dasha Tree</option>
                    <option value="FORECAST_DOMAINS">Current Phase & Forecast</option>
                    <option value="ASHTAKVARGA">Ashtakvarga SAV Matrix</option>
                    <option value="PLANET_TABLE">Planet Inspector Table</option>
                  </optgroup>
                </select>
              </div>

              {/* Fullscreen Maximize Toggle */}
              <button
                onClick={() => setMaximizedPaneId(pane.id)}
                className="p-1 rounded hover:bg-astro-bg text-astro-secondary-text hover:text-astro-primary cursor-pointer"
                title="Maximize Pane"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Pane Content Body */}
            <div className="flex-1">
              {renderPaneContent(pane.contentType)}
            </div>
          </div>
        ))}
      </div>

      {/* MAXIMIZED PANE FULLSCREEN MODAL */}
      {maximizedPaneId !== null && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs p-4 sm:p-8 flex items-center justify-center">
          <div className="bg-astro-bg border-2 border-astro-primary rounded-3xl w-full max-w-5xl h-[90vh] p-6 flex flex-col justify-between shadow-2xl overflow-hidden space-y-4">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <span className="font-bold text-lg font-serif text-astro-primary">
                  Maximized View: Slot #{maximizedPaneId}
                </span>
              </div>
              <button
                onClick={() => setMaximizedPaneId(null)}
                className="p-2 rounded-xl bg-astro-card hover:bg-astro-primary hover:text-white font-bold text-xs flex items-center gap-1 cursor-pointer transition-all"
              >
                <Minimize2 className="w-4 h-4" />
                <span>Exit Fullscreen</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
              {renderPaneContent(panes.find(p => p.id === maximizedPaneId)?.contentType || 'D1')}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
